import type { Timestamp } from 'firebase/firestore'
import type { IntegrationProvider } from './schema'
import type { BudgetEntityRef } from './budget'

export type FormLifecycleStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED'
export type FormResponseStatus = 'PENDING' | 'REVIEWED' | 'EXPORTED' | 'REJECTED'
export type FormFieldType = 'TEXT' | 'NUMBER' | 'DATE' | 'DROPDOWN' | 'CHECKBOX' | 'FILE_UPLOAD' | 'PERSON'
export type FormSort = 'NEWEST' | 'OLDEST' | 'TITLE'

export type TextValidation = { minLength?: number; maxLength?: number; pattern?: string }
export type NumberValidation = { min?: number; max?: number; step?: number }
export type DateValidation = { minDate?: string; maxDate?: string }
export type SelectionValidation = { minSelections?: number; maxSelections?: number }
export type FileValidation = { acceptedMimeTypes: string[]; maxFileSizeBytes: number; maxFiles: number }

type FormFieldBase = {
  id: string
  type: FormFieldType
  label: string
  description?: string
  required: boolean
  order: number
  step?: number
  bindings?: FormFieldBindings
}

export type FormFieldBindsTo =
  | { kind: 'STOCK'; itemId: string; quantityFieldRef: string; direction?: 'ADD' | 'REMOVE'; locationId?: string }
  | { kind: 'BUDGET'; entityRef: BudgetEntityRef; amountFieldRef: string }

export interface FormFieldBindings {
  bindsTo?: FormFieldBindsTo
}

export type TextFormField = FormFieldBase & {
  type: 'TEXT'
  placeholder?: string
  multiline?: boolean
  validation: TextValidation
}

export type NumberFormField = FormFieldBase & {
  type: 'NUMBER'
  placeholder?: string
  validation: NumberValidation
}

export type DateFormField = FormFieldBase & {
  type: 'DATE'
  validation: DateValidation
}

export type DropdownFormField = FormFieldBase & {
  type: 'DROPDOWN'
  placeholder?: string
  options: Array<{ id: string; label: string; value: string }>
  allowMultiple?: boolean
  presentation?: 'SELECT' | 'RADIO'
  validation: SelectionValidation
}

export type CheckboxFormField = FormFieldBase & {
  type: 'CHECKBOX'
  checkboxLabel?: string
  validation: Record<string, never>
}

export type FileUploadFormField = FormFieldBase & {
  type: 'FILE_UPLOAD'
  validation: FileValidation
}

export type PersonFieldPickScope = 'MEMBERS' | 'MEMBERS_AND_HELPERS'

export type PersonFormField = FormFieldBase & {
  type: 'PERSON'
  allowMultiple?: boolean
  pickScope?: PersonFieldPickScope
  validation: SelectionValidation
}

export type FormField =
  | TextFormField
  | NumberFormField
  | DateFormField
  | DropdownFormField
  | CheckboxFormField
  | FileUploadFormField
  | PersonFormField

export type FormLogicOperator = 'EQUALS' | 'NOT_EQUALS' | 'CONTAINS' | 'GREATER_THAN' | 'LESS_THAN'
export type FormLogicAction = 'SHOW' | 'HIDE' | 'REQUIRE' | 'SKIP_TO'

export type FormLogicRule = {
  id: string
  when: { fieldId: string; operator: FormLogicOperator; value: unknown }
  then: { action: FormLogicAction; targetFieldId: string }
}

export type FormLogic = { version: 1; rules: FormLogicRule[] }

export type FormTheme = {
  accentColor?: string
  backgroundColor?: string
  textColor?: string
  density?: 'COMPACT' | 'COMFORTABLE'
  logoUrl?: string | null
  customCss?: string | null
  /** Design Hub Bound engine — Phase B additive theming. Renderer falls back gracefully if these ids resolve to missing assets. */
  designAssetId?: string | null
  heroDesignAssetId?: string | null
  sectionSurfacePreset?: string | null
  layoutVariant?: 'DEFAULT' | 'DESIGN_WRAPPED'
}

export interface FormDefinition {
  id: string
  workspaceId: string
  title: string
  titleSearch: string
  titlePrefixes: string[]
  description: string
  fields: FormField[]
  logic: FormLogic
  theme: FormTheme
  lifecycleStatus: FormLifecycleStatus
  submissionCount: number
  lastSubmissionResponseId?: string | null
  deadline: Timestamp | Date | null
  isLocked: boolean
  createdBy: string
  createdAt: Timestamp | Date | null
  updatedAt: Timestamp | Date | null
}

export const canManageForm = (
  form: { createdBy?: string },
  access: { restricted: boolean; uid: string },
): boolean => !access.restricted || form.createdBy === access.uid

export type FormDefinitionDraft = Omit<
  FormDefinition,
  'titleSearch' | 'titlePrefixes' | 'submissionCount' | 'createdAt' | 'updatedAt'
> & { submissionCount?: number }

export interface FormResponse {
  id: string
  workspaceId: string
  formId: string
  submittedBy: string
  submittedByName?: string | null
  data: Record<string, unknown>
  status: FormResponseStatus
  sourceItems?: FormRequestSourceItem[]
  /** Server-written stock and budget effects associated with this response. */
  sideEffects?: FormResponseSideEffects
  submittedAt: Timestamp | Date | null
  rejectionReason?: string
  rejectedAt?: Timestamp | Date | null
}

export interface FormResponseSideEffects {
  stockDecrements?: Array<{ itemId: string; quantity: number; appliedAt: Timestamp }>
  budgetSpends?: Array<{ entityRef: BudgetEntityRef; amountMinor: number; appliedAt: Timestamp }>
  /** ISO timestamp written after the server has attempted every field binding. */
  processedAt?: string
}

export type FormUploadedFile = {
  fileId: string
  fileName: string
  sizeBytes: number
  mimeType: string
  downloadUrl: string
  storagePath: string
  isUnavailable?: boolean
  unavailableReason?: string
}

export type FormRequestStatus = 'PENDING' | 'COMPLETED' | 'CANCELLED' | 'EXPIRED'
export type FormRequestSourceItem = {
  itemId: string
  itemType: 'GOAL' | 'TASK' | 'SUBTASK' | 'CHECKLIST' | 'WORKSPACE'
  itemTitle: string
}
export interface FormRequest {
  id: string
  workspaceId: string
  formId: string
  formTitle: string
  recipientId: string
  recipientName: string
  requestedBy: string
  requestedByName: string
  status: FormRequestStatus
  responseId?: string | null
  sourceItems?: FormRequestSourceItem[]
  requestedAt: Timestamp | Date | null
  completedAt?: Timestamp | Date | null
  expiresAt: Timestamp | Date | null
}

export interface FormTemplate {
  id: string
  workspaceId: string | null
  scope: 'GLOBAL' | 'WORKSPACE'
  title: string
  description: string
  category: string
  fields: FormField[]
  logic: FormLogic
  theme: FormTheme
  createdAt: Timestamp | Date | null
  updatedAt: Timestamp | Date | null
}

export type FormProviderExportRequest = {
  workspaceId: string
  responseId: string
  providerId: Extract<IntegrationProvider, 'NOTION' | 'GOOGLE_SHEETS'>
}

export const normalizeFormTitle = (title: string) => title.trim().toLocaleLowerCase()

export const buildTitlePrefixes = (title: string) => {
  const normalized = normalizeFormTitle(title)
  return Array.from({ length: Math.min(normalized.length, 64) }, (_, index) => normalized.slice(0, index + 1))
}

export const createFormField = (type: FormFieldType, order: number, multiline = false): FormField => {
  const base = { id: crypto.randomUUID(), type, label: multiline ? 'Paragraph' : type.replace('_', ' ').toLocaleLowerCase().replace(/^./, (letter) => letter.toUpperCase()), required: false, order }
  if (type === 'TEXT') return { ...base, type, placeholder: '', multiline, validation: {} }
  if (type === 'NUMBER') return { ...base, type, placeholder: '', validation: {} }
  if (type === 'DATE') return { ...base, type, validation: {} }
  if (type === 'DROPDOWN') return { ...base, type, placeholder: 'Select an option', presentation: 'SELECT', options: [{ id: crypto.randomUUID(), label: 'Option 1', value: 'option-1' }], validation: {} }
  if (type === 'CHECKBOX') return { ...base, type, checkboxLabel: 'I agree', validation: {} }
  if (type === 'PERSON') return { ...base, type, allowMultiple: false, validation: {} }
  return { ...base, type: 'FILE_UPLOAD', validation: { acceptedMimeTypes: [], maxFileSizeBytes: 10 * 1024 * 1024, maxFiles: 1 } }
}

export const validateFormDefinitionDraft = (draft: FormDefinitionDraft): string[] => {
  const errors: string[] = []
  if (!draft.id) errors.push('Form ID is required.')
  if (!draft.workspaceId) errors.push('Workspace ID is required.')
  if (!draft.title.trim()) errors.push('Form title is required.')
  if (draft.title.trim().length > 160) errors.push('Form title must be 160 characters or fewer.')
  if (typeof draft.description !== 'string') errors.push('Description must be text.')
  if (!draft.theme || typeof draft.theme !== 'object') errors.push('Theme must be an object.')
  if (!draft.createdBy) errors.push('Form creator is required.')
  if (!Array.isArray(draft.fields)) errors.push('Fields must be an array.')
  if (draft.logic.version !== 1 || !Array.isArray(draft.logic.rules)) errors.push('Conditional logic must use schema version 1.')
  if (!['DRAFT', 'ACTIVE', 'ARCHIVED'].includes(draft.lifecycleStatus)) errors.push('Lifecycle status is invalid.')
  const ids = new Set<string>()
  draft.fields.forEach((field, index) => {
    if (!field.id || ids.has(field.id)) errors.push(`Field ${index + 1} must have a unique ID.`)
    ids.add(field.id)
    if (!field.label.trim()) errors.push(`Field ${index + 1} requires a label.`)
    if (!['TEXT', 'NUMBER', 'DATE', 'DROPDOWN', 'CHECKBOX', 'FILE_UPLOAD', 'PERSON'].includes(field.type)) errors.push(`Field ${index + 1} has an unsupported type.`)
    if (field.order !== index) errors.push(`Field ${index + 1} has an invalid order.`)
    if (field.step !== undefined && (!Number.isInteger(field.step) || field.step < 0)) errors.push(`${field.label}: step must be a non-negative integer.`)
    if (!field.validation || typeof field.validation !== 'object') errors.push(`Field ${index + 1} requires validation settings.`)
    if (field.type === 'NUMBER' && field.validation.min !== undefined && field.validation.max !== undefined && field.validation.min > field.validation.max) errors.push(`${field.label}: minimum cannot exceed maximum.`)
    if (field.type === 'DATE' && field.validation.minDate && field.validation.maxDate && field.validation.minDate > field.validation.maxDate) errors.push(`${field.label}: minimum date cannot exceed maximum date.`)
    if (field.type === 'PERSON') {
      if (field.validation.minSelections !== undefined && field.validation.minSelections < 0) errors.push(`${field.label}: minimum selections cannot be negative.`)
      if (field.validation.maxSelections !== undefined && field.validation.maxSelections < 1) errors.push(`${field.label}: maximum selections must be at least one.`)
      if (field.validation.minSelections !== undefined && field.validation.maxSelections !== undefined && field.validation.minSelections > field.validation.maxSelections) errors.push(`${field.label}: minimum selections cannot exceed maximum selections.`)
    }
    if (field.type === 'DROPDOWN') {
      if (field.options.length === 0) errors.push(`${field.label}: add at least one option.`)
      const optionValues = new Set<string>()
      field.options.forEach((option, optionIndex) => {
        if (!option.id) errors.push(`${field.label}: option ${optionIndex + 1} requires an ID.`)
        if (!option.label.trim()) errors.push(`${field.label}: option ${optionIndex + 1} requires a label.`)
        if (!option.value.trim()) errors.push(`${field.label}: option ${optionIndex + 1} requires a value.`)
        if (optionValues.has(option.value)) errors.push(`${field.label}: option values must be unique.`)
        optionValues.add(option.value)
      })
      if (field.validation.minSelections !== undefined && field.validation.minSelections < 0) errors.push(`${field.label}: minimum selections cannot be negative.`)
      if (field.validation.maxSelections !== undefined && field.validation.maxSelections < 1) errors.push(`${field.label}: maximum selections must be at least one.`)
      if (field.validation.minSelections !== undefined && field.validation.maxSelections !== undefined && field.validation.minSelections > field.validation.maxSelections) errors.push(`${field.label}: minimum selections cannot exceed maximum selections.`)
    }
    if (field.type === 'TEXT' && field.validation.pattern) {
      try { new RegExp(field.validation.pattern) } catch { errors.push(`${field.label}: regular expression is invalid.`) }
    }
  })
  draft.logic.rules.forEach((rule) => {
    if (!ids.has(rule.when.fieldId) || !ids.has(rule.then.targetFieldId)) errors.push('Conditional logic references a missing field.')
  })
  return errors
}
